// feature/litisoft-inline-dropdowns-v1 — sprint83.2.
//
// The 3 mandatory LITISOFT fields rendered inline in the case detail
// view so abogados can fill them where they already work. Mirrors the
// dropdowns inside the larger LitisoftFields panel (which lives in
// CaseDetail General tab and intake screens) but ships them
// standalone, role-agnostic, with auto-save on change.
//
// Anchor: the wrapping <section id="litisoft-mandatory-section"> is
// the smooth-scroll target for LitisoftIncompleteModal's "Ir a la
// sección" button. Mount with id={ANCHOR_ID} once per page.

import React, { useState } from "react";
import {
  PRETENSION_VALUES,
  CALIDAD_PARTE_PREVISORA_VALUES,
  TIPO_NOTIFICACION_VALUES,
  LITISOFT_REQUIRED_FIELDS,
  isLitisoftFieldMissing,
} from "./LitisoftFields";
import { cases as casesApi } from "../api/endpoints";
import { useToast } from "./Toast";

export const LITISOFT_MANDATORY_ANCHOR_ID = "litisoft-mandatory-section";

// LITISOFT_GATE_DEPLOYED_AT mirror so we can decide whether to render
// the red-tinted gated treatment. Backend env value is the source of
// truth; this is purely a visual hint on the frontend.
function isGated(caseData, gateDeployedAt) {
  if (!caseData?.created_at) return false;
  if (!gateDeployedAt) return false;
  return new Date(caseData.created_at) >= new Date(gateDeployedAt);
}

export default function LitisoftMandatoryInline({
  caseData, onSaved,
  // optional override; falls back to window.__LITISOFT_GATE_DEPLOYED_AT
  // (set by AppShell from /me, see TopBar/AuthContext if we wire it later).
  // For now this stays unset → defaults to "render fields but no red
  // gated styling" for grandfathered cases.
  gateDeployedAt,
  // Field names that should render with red outline (handed in by the
  // contestada-error flow so the user knows which to fill).
  highlightMissing = [],
}) {
  const toast = useToast();
  const c = caseData || {};
  const [busyField, setBusyField] = useState(null);

  // Local optimistic state so the select reflects the choice the instant
  // it's made; the request fires in the background. On error, we revert.
  const [local, setLocal] = useState({
    pretension:              c.pretension || "",
    calidad_parte_previsora: c.calidad_parte_previsora || "",
    tipo_notificacion:       c.tipo_notificacion || "",
  });

  async function save(field, value) {
    const previous = local[field];
    setLocal((s) => ({ ...s, [field]: value }));
    setBusyField(field);
    try {
      await casesApi.update(c.id, { [field]: value || null });
      onSaved?.();
    } catch (e) {
      setLocal((s) => ({ ...s, [field]: previous }));
      toast.push(e?.response?.data?.error || "Error al guardar", "error");
    } finally {
      setBusyField(null);
    }
  }

  const gated = isGated(c, gateDeployedAt);
  const missingSet = new Set(highlightMissing || []);

  // If gated, surface a per-field amber background when the field is
  // null. The red outline (highlightMissing) takes precedence and is
  // driven by the contestada error response.
  const fieldStyle = (k) => {
    if (missingSet.has(k)) {
      return { outline: "2px solid var(--color-danger)", outlineOffset: 1, borderRadius: 4 };
    }
    if (gated && isLitisoftFieldMissing(local[k], k)) {
      return { background: "var(--color-warning-bg)" };
    }
    return undefined;
  };

  const missingNow = LITISOFT_REQUIRED_FIELDS.filter((f) =>
    isLitisoftFieldMissing(local[f], f)).length;

  return (
    <section
      id={LITISOFT_MANDATORY_ANCHOR_ID}
      className="card tight"
      style={{
        marginTop: 16,
        borderLeft: gated && missingNow > 0
          ? "3px solid var(--color-warning)"
          : "3px solid var(--accent-mint)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Datos LITISOFT obligatorios</h3>
        {missingNow > 0 ? (
          <span className="badge" style={{
            fontSize: 10, padding: "2px 8px",
            background: "var(--color-warning-bg)", color: "var(--color-warning-ink)",
            border: "1px solid var(--color-warning)", borderRadius: 10,
          }}>
            Incompleto ({3 - missingNow}/3)
          </span>
        ) : (
          <span className="badge" style={{
            fontSize: 10, padding: "2px 8px",
            background: "var(--color-success-bg)", color: "var(--accent-mint)",
            border: "1px solid var(--accent-mint)", borderRadius: 10,
          }}>
            Completo ✓
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        3 campos obligatorios antes de marcar la contestación. Se guarda automáticamente al elegir.
      </div>

      <div className="grid-2" style={{ marginTop: 12 }}>
        <div>
          <label>
            Pretensión <span style={{ color: "var(--color-danger)" }}>*</span>
          </label>
          <select value={local.pretension}
                  disabled={busyField === "pretension"}
                  onChange={(e) => save("pretension", e.target.value)}
                  style={fieldStyle("pretension")}>
            <option value="">— Seleccionar —</option>
            {PRETENSION_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
            {local.pretension && !PRETENSION_VALUES.includes(local.pretension) && (
              <option value={local.pretension}>{local.pretension} (valor anterior)</option>
            )}
          </select>
        </div>
        <div>
          <label>
            Calidad de Parte Previsora <span style={{ color: "var(--color-danger)" }}>*</span>
          </label>
          <select value={local.calidad_parte_previsora}
                  disabled={busyField === "calidad_parte_previsora"}
                  onChange={(e) => save("calidad_parte_previsora", e.target.value)}
                  style={fieldStyle("calidad_parte_previsora")}>
            <option value="">— Seleccionar —</option>
            {CALIDAD_PARTE_PREVISORA_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
            {local.calidad_parte_previsora && !CALIDAD_PARTE_PREVISORA_VALUES.includes(local.calidad_parte_previsora) && (
              <option value={local.calidad_parte_previsora}>{local.calidad_parte_previsora} (valor anterior)</option>
            )}
          </select>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label>
            Tipo de Notificación <span style={{ color: "var(--color-danger)" }}>*</span>
          </label>
          <select value={local.tipo_notificacion}
                  disabled={busyField === "tipo_notificacion"}
                  onChange={(e) => save("tipo_notificacion", e.target.value)}
                  style={fieldStyle("tipo_notificacion")}>
            <option value="">— Seleccionar —</option>
            {TIPO_NOTIFICACION_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
            {local.tipo_notificacion && !TIPO_NOTIFICACION_VALUES.includes(local.tipo_notificacion) && (
              <option value={local.tipo_notificacion}>{local.tipo_notificacion} (valor anterior)</option>
            )}
          </select>
        </div>
      </div>
    </section>
  );
}

// Smooth-scroll the page to the inline section and (optionally) flash
// any missing-field outlines. Used by LitisoftIncompleteModal.
export function scrollToLitisoftMandatorySection() {
  const el = document.getElementById(LITISOFT_MANDATORY_ANCHOR_ID);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}
